import FetchDocumentIndicators from '@Application/Indicators/Queries/FetchDocumentIndicators';
import buildIndicatorQueryKey from '@Application/Indicators/buildIndicatorQueryKey';
import fetchDocumentIndicators from '@Application/Indicators/fetchDocumentIndicators';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import ComparisonLine from '@UI/Pages/Home/Shared/ComparisonLine';
import DocumentTypeFilter from '@UI/Pages/Home/DocumentsBlock/DocumentTypeFilter';
import TypeDistribution from '@UI/Pages/Home/DocumentsBlock/TypeDistribution';
import VisuallyHiddenText from '@UI/Pages/Home/Shared/VisuallyHiddenText';
import IndicatorBlockState from '@UI/Pages/Home/Shared/IndicatorBlockState';
import IndicatorCard from '@UI/Pages/Home/Shared/IndicatorCard';
import formatCount from '@UI/Pages/Home/Helpers/formatCount';
import formatIndicatorPeriod from '@UI/Pages/Home/Helpers/formatIndicatorPeriod';
import useHomeIndicatorScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope';
import useRetainedIndicatorView from '@UI/Pages/Home/Hooks/useRetainedIndicatorView';
import useOwnershipWording from '@UI/Pages/Home/Hooks/useOwnershipWording';
import useWidenIndicatorAction from '@UI/Pages/Home/Hooks/useWidenIndicatorAction';
import homeDocumentTypeSignal from '@UI/Pages/Home/Signals/homeDocumentTypeSignal';
import homePeriodSignal from '@UI/Pages/Home/Signals/homePeriodSignal';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const figureStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

const figureRowStyle = css`
	margin: 0;
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 0.75rem;
`;

const bigNumberStyle = css`
	font-family: ${theme.fonts.display};
	font-size: 4.75rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 0.95;
	letter-spacing: -0.045em;
	color: ${theme.tints.teal.ink};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		font-size: 3.625rem;
	}
`;

const unitStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 1.0625rem;
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.ink};
`;

const bodyStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.125rem;
`;

/** Documents generated over the window, with the per-type breakdown and the type filter. */
const DocumentsBlock: React.FC = () => {
	const dispatch = useDispatch();
	const period = useSignalValue(homePeriodSignal);
	const documentTypeId = useSignalValue(homeDocumentTypeSignal);
	const { status, indicatorScope } = useHomeIndicatorScope();
	const isOwnershipWording = useOwnershipWording();
	const widenAction = useWidenIndicatorAction(true);
	const {
		documentTypes,
		error: documentTypesError,
		refetch: refetchDocumentTypes,
	} = useAccountDocumentTypes();

	const queryKey =
		indicatorScope === null ? null : buildIndicatorQueryKey(indicatorScope, period, documentTypeId);

	const {
		data,
		error: indicatorsError,
		refetch: refetchIndicators,
	} = useStoreQuery({
		selector: (state) => state.indicatorsStore.documents,
		stopOn: (view) => queryKey === null || (view !== null && view.queryKey === queryKey),
		query: () => {
			if (indicatorScope === null) {
				return Promise.resolve();
			}

			return dispatch(
				fetchDocumentIndicators(
					new FetchDocumentIndicators({ scope: indicatorScope, period, documentTypeId }),
				),
			);
		},
		deps: [queryKey],
	});

	const {
		view,
		context: shownTypeId,
		isRefreshing,
	} = useRetainedIndicatorView(data, queryKey, documentTypeId);

	const formattedPeriod = React.useMemo(() => {
		if (view === null) {
			return null;
		}

		return formatIndicatorPeriod(view.period);
	}, [view]);

	const handleRetry = React.useCallback(() => {
		refetchIndicators();
		refetchDocumentTypes();
	}, [refetchIndicators, refetchDocumentTypes]);

	const title = isOwnershipWording ? 'Mes documents générés' : 'Documents générés';
	const tooltip = `Documents produits par l'IA ${
		formattedPeriod === null
			? 'sur la période'
			: `entre le ${formattedPeriod.from} et le ${formattedPeriod.to}`
	} (heure de Paris), regénérations comprises, quel que soit leur état de vérification, hors dossiers supprimés.`;

	const renderBody = (): React.ReactNode => {
		if (status === 'unavailable') {
			return <IndicatorBlockState kind="empty" title="Aucune agence ne vous est rattachée." />;
		}

		if (indicatorsError !== null || documentTypesError !== null) {
			return <IndicatorBlockState kind="error" onRetry={handleRetry} />;
		}

		if (view === null || documentTypes === null) {
			return <IndicatorBlockState kind="loading" variant="documents" />;
		}

		const range = `${formattedPeriod?.from} – ${formattedPeriod?.to} (heure de Paris)`;

		if (view.generated.current === 0) {
			return (
				<IndicatorBlockState
					kind="empty"
					title={
						shownTypeId === null
							? 'Aucun document généré sur la période'
							: 'Aucun document de ce type sur la période'
					}
					range={range}
					{...(widenAction === null
						? {}
						: { actionLabel: widenAction.label, onAction: widenAction.run })}
				/>
			);
		}

		const count = view.generated.current;
		const typeSuffix = shownTypeId === null ? '' : ` · ${documentTypes.labelOf(shownTypeId)}`;
		const unit = `${count > 1 ? 'documents générés' : 'document généré'}${typeSuffix}`;

		return (
			<div css={bodyStyle}>
				<div css={figureStyle}>
					<p css={figureRowStyle}>
						<span css={bigNumberStyle} aria-hidden="true">
							{formatCount(count)}
						</span>
						<span css={unitStyle} aria-hidden="true">
							{unit}
						</span>
						<VisuallyHiddenText>{`${formatCount(count)} ${unit}`}</VisuallyHiddenText>
					</p>
					<ComparisonLine comparison={view.generated} days={view.period.days} subject="document" />
				</div>
				<TypeDistribution byType={view.byType} documentTypes={documentTypes} />
			</div>
		);
	};

	return (
		<IndicatorCard
			title={title}
			tooltip={tooltip}
			band={theme.home.indicatorBand.documents}
			isRefreshing={isRefreshing && indicatorsError === null && documentTypesError === null}
			actions={
				documentTypes === null ? (
					<Skeleton width="9rem" height="2.3125rem" borderRadius={theme.radii.pill} />
				) : (
					<DocumentTypeFilter documentTypes={documentTypes} byType={view?.byType ?? []} />
				)
			}
		>
			{renderBody()}
		</IndicatorCard>
	);
};

export default DocumentsBlock;
